import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Wand2, ArrowLeft, Target, Users, Shield, Zap, Brain, Globe } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Link>
              </Button>
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-primary">About Us</h1>
                <p className="text-sm text-muted-foreground">Learn about AI Humanizer Text</p>
              </div>
            </div>
            <Navigation />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-6">
            Making AI Content More Human
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            AI Humanizer Text bridges the gap between artificial intelligence and authentic human communication, 
            transforming robotic AI-generated content into natural, engaging writing that connects with readers.
          </p>
        </div>

        {/* Mission Section */}
        <section className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6">Our Mission</h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                In an era where AI-generated content is becoming increasingly common, we believe that technology should enhance 
                human expression, not replace it. Our mission is to provide tools that make AI-generated text feel more authentic, 
                personal, and engaging.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We're committed to helping content creators, students, professionals, and businesses communicate more effectively 
                by transforming stilted AI prose into natural, human-like writing that resonates with audiences.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="text-center">
                  <Target className="w-8 h-8 text-primary mx-auto mb-2" />
                  <CardTitle className="text-lg">Precision</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground text-center">
                    Advanced AI algorithms ensure accurate humanization
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="text-center">
                  <Brain className="w-8 h-8 text-primary mx-auto mb-2" />
                  <CardTitle className="text-lg">Intelligence</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground text-center">
                    Continuously learning and improving our models
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-12">Our Core Values</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardHeader className="text-center">
                <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
                <CardTitle className="text-xl">Privacy & Security</CardTitle>
                <CardDescription>Your content is safe with us</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-center">
                  We implement industry-leading security measures to protect your data. 
                  Your text is processed securely and never permanently stored.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
                <CardTitle className="text-xl">Speed & Efficiency</CardTitle>
                <CardDescription>Fast, reliable processing</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-center">
                  Our optimized algorithms deliver high-quality humanized text in seconds, 
                  helping you maintain productivity and meet deadlines.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                <CardTitle className="text-xl">User-Centric Design</CardTitle>
                <CardDescription>Built with you in mind</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-center">
                  Every feature is designed to make your experience smooth and intuitive, 
                  from simple text input to advanced customization options.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Technology Section */}
        <section className="mb-16">
          <div className="bg-muted rounded-lg p-8">
            <div className="text-center mb-8">
              <Globe className="w-16 h-16 text-primary mx-auto mb-4" />
              <h3 className="text-3xl font-bold mb-4">Advanced AI Technology</h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our platform leverages cutting-edge natural language processing models to understand context, 
                tone, and style, ensuring each humanization maintains the original meaning while improving readability.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              <div>
                <h4 className="font-semibold mb-3">What Makes Us Different</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Context-aware processing that preserves meaning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Multiple intensity levels for different use cases</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Real-time processing with instant results</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Continuous model improvements and updates</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-3">Use Cases</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Content marketing and blog posts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Academic and research writing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Email and business communication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span>Social media and creative content</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Ready to Transform Your AI Content?</CardTitle>
              <CardDescription className="text-lg">
                Join thousands of users who trust AI Humanizer Text to make their content more engaging and authentic.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/">
                    Start Humanizing Text
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/pricing">
                    View Pricing Plans
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default About;